import { useCallback, useEffect, useState } from "react";
import type { JSX } from "react";
import { Icon } from "../ui";
import type { IconName } from "../ui";
import { useAppLogs } from "../../hooks/useAppLogs";
import type { LogEntity } from "../../hooks/useAppLogs";

const LEVEL_COLORS: Record<string, string> = {
  error: "text-red-400",
  warn: "text-yellow-400",
  info: "text-blue-400",
  debug: "text-content-tertiary",
};

const LEVEL_ICONS: Record<string, IconName> = {
  error: "circle-alert",
  warn: "shield-alert",
  info: "circle-dot",
  debug: "code",
};

interface LogViewerPopoverProps {
  readonly onClose: () => void;
}

export function LogViewerPopover({ onClose }: LogViewerPopoverProps): JSX.Element {
  const { queryLogs } = useAppLogs();
  const [logs, setLogs] = useState<readonly LogEntity[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [levelFilter, setLevelFilter] = useState<string>("");

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    const params: Record<string, unknown> = { limit: 100 };
    if (levelFilter) params.level = levelFilter;
    const result = await queryLogs(params);
    setLogs(result.logs);
    setTotal(result.total);
    setLoading(false);
  }, [queryLogs, levelFilter]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchLogs();
  }, [fetchLogs]);

  return (
    <div
      className="absolute bottom-full right-0 mb-1 w-[560px] max-h-[420px]
        bg-surface-secondary border border-edge rounded-lg shadow-lg z-50 flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-edge shrink-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-content">Logs</span>
          <span className="text-content-tertiary text-[11px] tabular-nums">{total} total</span>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={levelFilter}
            onChange={(e) => { setLevelFilter(e.target.value); }}
            className="text-[11px] bg-surface-tertiary border border-edge rounded px-1.5 py-0.5
              text-content-secondary outline-none"
          >
            <option value="">All levels</option>
            <option value="error">Errors</option>
            <option value="warn">Warnings</option>
            <option value="info">Info</option>
            <option value="debug">Debug</option>
          </select>
          <button
            type="button"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={fetchLogs}
            className="text-content-tertiary hover:text-content bg-transparent border-none cursor-pointer"
            title="Refresh"
          >
            <Icon name="loader" size={13} />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="text-content-tertiary hover:text-content bg-transparent border-none cursor-pointer"
          >
            <Icon name="close" size={14} />
          </button>
        </div>
      </div>

      {/* Log entries */}
      <div className="overflow-y-auto flex-1 min-h-0">
        {loading ? (
          <div className="flex items-center justify-center py-8 text-content-tertiary">
            <Icon name="loader" size={16} className="animate-spin mr-2" />
            Loading…
          </div>
        ) : logs.length === 0 ? (
          <div className="text-content-tertiary px-3 py-8 text-center">No logs</div>
        ) : (
          logs.map((log) => (
            <div
              key={log.id}
              className="flex items-start gap-2 px-3 py-1.5 border-b border-edge/30
                hover:bg-surface-hover/20 text-[11px]"
            >
              <Icon
                name={LEVEL_ICONS[log.level] ?? "circle-dot"}
                size={12}
                className={`mt-0.5 shrink-0 ${LEVEL_COLORS[log.level] ?? ""}`}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className={`font-medium ${LEVEL_COLORS[log.level] ?? ""}`}>
                    {log.level.toUpperCase()}
                  </span>
                  <span className="text-content-tertiary">
                    {log.origin.kind}/{log.origin.id}
                  </span>
                  <span className="text-content-tertiary opacity-50 ml-auto shrink-0">
                    {formatTimestamp(log.timestamp)}
                  </span>
                </div>
                <div className="text-content-secondary mt-0.5">{log.message}</div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch {
    return iso;
  }
}
